// Integración con el SDK de Rebill (web components, Stencil). El componente
// `<rebill-checkout>` renderiza un checkout de suscripción EMBEBIDO usando solo
// el PUBLIC KEY (seguro para el front) + un `plan-id` del panel. No hace falta
// backend ni credenciales secretas para el checkout (el backend procesa webhooks).
//
// Este módulo registra los custom elements del SDK una sola vez (`ensureRebillSDK`).
// Docs: https://docs.rebill.com/sdk/checkout

import { REBILL_ENV } from '../config';

/**
 * Memoiza una operación async: corre una sola vez y todos los llamadores
 * comparten la misma promesa — pero si FALLA, se resetea para que el próximo
 * llamado reintente de verdad.
 *
 * El reseteo es la parte importante: antes acá había un flag booleano que se
 * ponía en `true` ANTES de los `await`, así que un import dinámico fallido
 * (red flaky, chunks con hash viejo tras un deploy) dejaba el flag puesto y
 * todo reintento "resolvía" al instante sin registrar el custom element →
 * `<rebill-checkout>` desconocido para el browser → checkout en blanco
 * silencioso hasta recargar. Ver ADR-2026-08-24-checkout-rebill-carga-silenciosa.
 */
export function memoizarConReintento(
  fn: () => Promise<void>,
): () => Promise<void> {
  let promesa: Promise<void> | null = null;
  return () => {
    if (!promesa) {
      promesa = fn().catch((err) => {
        promesa = null;
        throw err;
      });
    }
    return promesa;
  };
}

/**
 * Registra los web components de Rebill (idempotente; reintenta si falló).
 * Debe correr en el browser antes de usar `<rebill-checkout>`.
 *
 * IMPORTANTE: usamos el bundle de *custom elements* (`rebill/rebill-checkout`),
 * que hace imports estáticos y es amigable con bundlers (Vite). NO usamos el
 * lazy loader (`rebill/loader` → `defineCustomElements`), que carga cada
 * componente por chunk `.entry.js` en runtime y rompe con el optimizador de
 * Vite (504 "Outdated Optimize Dep" + "Constructor ... was not found").
 */
export const ensureRebillSDK: () => Promise<void> = memoizarConReintento(
  async () => {
    if (typeof window === 'undefined') return;

    // Configura el entorno del SDK (host de la API + assets desde el CDN).
    // Importamos de 'rebill/config' (barrel completo); el entry '.' del paquete
    // apunta solo al componente y no expone `initializeRebillSDK`.
    const { initializeRebillSDK } = await import('rebill/config');
    initializeRebillSDK({ environment: REBILL_ENV, debug: REBILL_ENV !== 'production' });

    // El subpath 'rebill/rebill-checkout' mapea sus TIPOS a index.d.ts (que no
    // exporta `defineCustomElement`), pero el archivo real sí lo exporta. Cast a
    // any para el destructuring; el runtime es correcto.
    const mod = (await import('rebill/rebill-checkout')) as any;
    if (!customElements.get('rebill-checkout')) {
      mod.defineCustomElement();
    }
    // Aseguramos que el custom element esté definido antes de usarlo.
    await customElements.whenDefined('rebill-checkout');

    // Referencia al store interno del SDK (misma instancia de módulo que usa el
    // checkout: `rebill-checkout.js` importa este mismo chunk). Un fallo acá NO
    // rompe el SDK: sin store, `limpiarDescuentoRebill` devuelve false y el
    // caller cae a la recarga de página de siempre.
    try {
      const storeMod = (await import('rebill/dist/components/p-B_gjprrz.js')) as any;
      if (typeof storeMod?.S?.setDiscount === 'function' && storeMod?.s) {
        storeRebill = { SessionStore: storeMod.S, state: storeMod.s };
      }
    } catch {
      storeRebill = null;
    }
  },
);

// ── Limpieza del descuento pegado en el store del SDK ────────────────────────
// ADR-2026-08-24-limpiar-descuento-rebill-sin-recargar (supersede en esto a
// ADR-2026-08-05-cupon-se-arrastra-entre-planes).
//
// El SDK guarda el cupón aplicado en un store de módulo que sobrevive al
// desmontaje del `<rebill-checkout>`. Su entry público no expone ningún reset,
// pero el chunk interno del store SÍ exporta `SessionStore` —cuyo
// `setDiscount(null)` es la rama de limpieza del propio SDK— y `state`, con el
// que se VERIFICA que quedó limpio.
//
// FRÁGIL A PROPÓSITO Y CON RED: el nombre del chunk lleva hash (`p-B_gjprrz.js`
// en rebill 1.17.26). Si se actualiza `rebill`, el import se rompe EN BUILD
// (ruidoso, no silencioso en runtime); hay que re-apuntarlo al chunk que exporte
// `state as s` y `SessionStore as S` (y actualizar `src/vite-env.d.ts`). En
// runtime, cualquier fallo o duda devuelve `false` y el caller
// (`RebillCardPayment`) recarga la página, que sigue siendo la garantía de fondo.

export interface StoreRebill {
  SessionStore: { setDiscount: (discount: unknown) => void };
  state: any;
}

let storeRebill: StoreRebill | null = null;

/**
 * Limpia el descuento del store del SDK usando su propio `setDiscount(null)` y
 * verifica leyendo el store que quedó limpio. `true` = garantizado limpio;
 * `false` = no se pudo garantizar (el caller debe recargar la página).
 */
export function limpiarDescuentoDeStoreRebill(
  store: StoreRebill | null,
): boolean {
  const state = store?.state;
  // ¿Es el store VIVO? Uno prístino —sin rastros de la sesión del checkout
  // anterior— puede ser una COPIA duplicada por el bundler (mismo archivo,
  // instancia de módulo distinta): limpiarla no limpiaría el store real del
  // SDK, y el "éxito" sería mentira. Ante la duda, false.
  if (!state?._session && !state?.data?.sessionId) return false;
  try {
    store!.SessionStore.setDiscount(null);
  } catch {
    // `setDiscount` limpia los campos ANTES de recalcular totales: aunque el
    // recálculo tire (sesión vieja rara), la verificación de abajo decide.
  }
  try {
    const pricing = state.data?.pricing;
    const discount = state.data?.discount;
    // Espejo de los campos que el SDK consulta: la re-adopción del
    // `discount-coupon` mira `pricing.discountCouponCode`/`discountAmount`, y
    // el cobro manda `discount.couponCode` en la transacción.
    return (
      !!pricing &&
      !pricing.discountAmount &&
      !pricing.discountCouponCode &&
      !discount?.couponCode &&
      !discount?.discountType
    );
  } catch {
    return false;
  }
}

/** Versión enchufada al store real, que carga `ensureRebillSDK`. */
export function limpiarDescuentoRebill(): boolean {
  return limpiarDescuentoDeStoreRebill(storeRebill);
}
